import { useState, useRef, useEffect, useCallback, useLayoutEffect, ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface PopoverProps {
    open: boolean
    onClose: () => void
    anchorEl: HTMLElement | null
    children: ReactNode
    placement?: 'bottom-start' | 'bottom-end' | 'bottom-center' | 'top-start' | 'top-end' | 'top-center'
    className?: string
    /** Match the popover's width to the anchor's width (e.g. a dropdown as wide as its trigger). */
    matchAnchorWidth?: boolean
}

export const Popover = ({ open, onClose, anchorEl, children, placement = 'bottom-start', className = '', matchAnchorWidth = false }: PopoverProps) => {
    const ref = useRef<HTMLDivElement>(null)
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
    const [anchorWidth, setAnchorWidth] = useState<number | null>(null)

    const updatePosition = useCallback(() => {
        if (!anchorEl) return
        const rect = anchorEl.getBoundingClientRect()
        const mh = ref.current?.offsetHeight ?? 300
        // Always position against the panel's MEASURED width. `matchAnchorWidth` sets `width` to the
        // anchor's width, but a `min-w-*`/`max-w-*` class in the caller's className can still make
        // the rendered panel wider or narrower — assuming `rect.width` there would offset a centered
        // panel by half the difference and mis-clamp it at the viewport edges.
        const mw = ref.current?.offsetWidth ?? (matchAnchorWidth ? rect.width : 280)
        if (matchAnchorWidth) setAnchorWidth(rect.width)

        let top: number
        let left: number

        if (placement.startsWith('bottom')) {
            top = rect.bottom + 4
        } else {
            top = rect.top - mh - 4
        }

        if (placement.endsWith('center')) {
            left = rect.left + rect.width / 2 - mw / 2
        } else if (placement.endsWith('start')) {
            left = rect.left
        } else {
            left = rect.right - mw
        }

        // Flip above the anchor when the panel would overflow the bottom, but never past the top of
        // the viewport — a panel taller than the space above it must stay reachable rather than
        // start at a negative offset with its top scrolled out of reach.
        if (top + mh > window.innerHeight - 8) {
            top = Math.max(8, rect.top - mh - 4)
        }
        left = Math.max(8, Math.min(left, window.innerWidth - mw - 8))

        setPosition({ top, left })
    }, [anchorEl, placement, matchAnchorWidth])

    // Reset position on close so each open re-measures BEFORE the portal (and its entry animation)
    // renders — otherwise the panel mounts at a stale/zero position and visibly slides in from the
    // wrong spot on first open, and the entry animation plays against the wrong coordinates.
    useLayoutEffect(() => {
        if (open) updatePosition()
        else {
            setPosition(null)
            setAnchorWidth(null)
        }
    }, [open, updatePosition])

    // Content can grow after the initial measurement — e.g. a picker nested in the popover revealing
    // its own calendar on click. Without this the panel keeps the position computed for its original
    // (shorter) height and can run off the bottom of the viewport instead of flipping.
    useEffect(() => {
        if (!open || !ref.current) return
        const observer = new ResizeObserver(() => updatePosition())
        observer.observe(ref.current)
        return () => observer.disconnect()
    }, [open, updatePosition])

    useEffect(() => {
        if (!open) return
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node
            if (ref.current?.contains(target) || anchorEl?.contains(target)) return
            onClose()
        }
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEscape)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleEscape)
        }
    }, [open, onClose, anchorEl])

    // A null anchor has nothing to position against: `updatePosition` bails, so `position` stays
    // null and the measure-then-show logic below would leave the portal mounted but permanently
    // hidden and inert. Fail deterministically instead — render nothing until the anchor exists.
    if (!open || !anchorEl) return null

    // Until measured, the portal renders hidden (so `ref` exists for an accurate measurement) at
    // placeholder coordinates. It must already be wearing the caller's className during that pass:
    // `updatePosition` reads offsetWidth/offsetHeight from exactly this render, and the sizing that
    // decides those (`w-*`, `max-h-*`) lives in className — measuring without it centers/flips the
    // panel against the wrong box. `visibility: hidden` (not a class swap) does the hiding: hidden
    // elements still lay out, so the measurement is real, and any entry animation the caller puts
    // in className is left intact to play once, from the correct coordinates.
    const positioned = position !== null

    return createPortal(
        <div
            ref={ref}
            className={`fixed z-[9999] rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:shadow-slate-900/50 ${className}`}
            style={{
                top: position?.top ?? 0,
                left: position?.left ?? 0,
                ...(matchAnchorWidth && anchorWidth != null ? { width: anchorWidth } : {}),
                ...(positioned ? {} : { visibility: 'hidden' as const, pointerEvents: 'none' as const }),
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {children}
        </div>,
        document.body
    )
}
